import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  Landmark,
  TrendingUp,
  ShieldCheck,
  CreditCard,
  Megaphone,
  MonitorSmartphone,
  PencilRuler,
  Clapperboard,
  PartyPopper,
  Gem,
  ArrowUpRight,
  CheckCircle2,
} from "lucide-react";
import { MaskLines, Reveal } from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import LeadForm from "@/components/LeadForm";
import CallbackDock from "@/components/CallbackDock";
import { Deliverables } from "@/components/Deliverables";
import { ScrollCta } from "@/components/PageHero";

const HERO_IMG =
  "https://static.prod-images.emergentagent.com/jobs/946b3673-7a0e-484a-808e-d45d9c8ae2d9/images/170cea362471aa5990d4e970b3d5ac5b028574098b6c00609cc8ba283bc99314.jpeg";

const PILLARS = [
  {
    n: "01",
    icon: Landmark,
    title: "Loans & Funding",
    desc: "Retail and commercial finance, structured end-to-end.",
    to: "/loans",
  },
  {
    n: "02",
    icon: TrendingUp,
    title: "Wealth Portfolios",
    desc: "SIPs, mutual funds, PMS and retirement planning.",
    to: "/wealth",
  },
  {
    n: "03",
    icon: ShieldCheck,
    title: "Insurance Solutions",
    desc: "Protection for families, assets and enterprises.",
    to: "/insurance",
  },
  {
    n: "04",
    icon: CreditCard,
    title: "Credit Card Liquidity",
    desc: "Fast, secure bill swiping and liquidity.",
    to: "/credit-card-liquidity",
  },
  {
    n: "05",
    icon: Megaphone,
    title: "Digital Marketing",
    desc: "High-intent B2B lead engines that maximise ROI.",
    to: "/digital-marketing",
  },
  {
    n: "06",
    icon: MonitorSmartphone,
    title: "IT Services",
    desc: "Websites, e-commerce, CRM and lead automation.",
    to: "/it-services",
  },
  {
    n: "07",
    icon: PencilRuler,
    title: "Interior Design",
    desc: "Residential and commercial spaces, turnkey.",
    to: "/interior-design",
  },
  {
    n: "08",
    icon: Clapperboard,
    title: "Videography & Photography",
    desc: "Weddings, concerts, brand films and drone cinematography.",
    to: "/videography-photography",
  },
  {
    n: "09",
    icon: PartyPopper,
    title: "Event Management",
    desc: "Weddings, concerts, conferences and celebrations — turnkey.",
    to: "/event-management",
  },
  {
    n: "10",
    icon: Gem,
    title: "Destination Weddings",
    desc: "Palace, beach and resort weddings, flawlessly curated.",
    to: "/destination-weddings",
  },
];

const MARQUEE_ITEMS = [
  "Loans & Funding",
  "Wealth Portfolios",
  "Insurance",
  "Credit Card Liquidity",
  "Digital Marketing",
  "IT Services",
  "Interior Design",
  "Videography & Photography",
  "Event Management",
  "Destination Weddings",
];

const STATS = [
  { value: "10+", label: "Service Pillars", desc: "Finance, digital, media, events and spaces — every desk under one roof." },
  { value: "20+", label: "Growth Directions", desc: "Capital, brand, technology and physical presence, moving together." },
  { value: "5+", label: "Integrated Teams", desc: "Specialist teams that share one brief, one timeline and one manager." },
  { value: "360°", label: "Growth", desc: "A complete view of your business — not a single transaction." },
];

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const yImg = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const yChip = useTransform(scrollYProgress, [0, 1], [0, -50]);

  return (
    <div data-testid="home-page">
      <CallbackDock />

      {/* HERO */}
      <section
        ref={heroRef}
        className="relative overflow-hidden pt-[76px]"
        data-testid="home-hero"
      >
        <div
          className="pointer-events-none absolute -left-40 top-24 h-[480px] w-[480px] rounded-full bg-gold/15 blur-[120px]"
          aria-hidden="true"
        />
        <div
          className="pointer-events-none absolute right-0 top-0 h-72 w-72 rotate-45 border border-gold/20"
          aria-hidden="true"
        />
        <div className="container-x relative grid grid-cols-1 items-center gap-14 py-14 lg:min-h-[calc(100vh-76px)] lg:grid-cols-12 lg:py-20">
          <div className="lg:col-span-7">
            <motion.p
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="eyebrow flex items-center gap-3"
              data-testid="hero-tagline"
            >
              <span className="inline-block h-px w-10 bg-amber-600/60" />
              Comprehensive Consultancy — Ahmedabad
            </motion.p>
            <MaskLines
              lines={[
                "EVOLVE",
                <>
                  ELITE<span className="text-gold">.</span>
                </>,
              ]}
              className="mt-4 font-display text-[19vw] font-black leading-[0.92] tracking-tight text-navy sm:text-8xl lg:text-[7.5rem]"
              delay={0.15}
            />
            <motion.p
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.65 }}
              className="mt-6 font-display text-xl font-bold text-navy sm:text-2xl"
              data-testid="hero-subheadline"
            >
              Building Wealth.{" "}
              <span className="text-amber-600">Scaling Brands.</span> Designing
              Spaces.
            </motion.p>
            <motion.p
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.78 }}
              className="mt-4 max-w-xl text-base leading-relaxed text-slate-600 sm:text-lg"
            >
              Ten specialist pillars — from loans and wealth to marketing,
              interiors, media and events — delivered through one seamless,
              single-window process you never have to chase.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.9 }}
              className="mt-9 flex flex-wrap items-center gap-4"
            >
              <ScrollCta
                href="#callback"
                label="Request a Callback"
                testId="hero-request-callback-btn"
              />
              <Link
                to="/about"
                className="btn-outline"
                data-testid="hero-about-link"
              >
                Our Story
              </Link>
            </motion.div>
          </div>

          <div className="relative lg:col-span-5">
            <motion.div
              style={{ y: yImg }}
              className="relative mx-auto max-w-md lg:max-w-none"
            >
              <div className="overflow-hidden rounded-b-[2rem] rounded-t-[999px] border-4 border-white shadow-[0_48px_90px_-30px_rgba(10,25,47,0.45)]">
                <img
                  src={HERO_IMG}
                  alt="Modern corporate architecture with navy glass and gold accents"
                  className="aspect-[3/4] w-full object-cover"
                  data-testid="hero-arch-image"
                />
              </div>
            </motion.div>
            <motion.div
              style={{ y: yChip }}
              className="absolute -left-2 top-14 rounded-2xl border border-slate-200 bg-white/95 px-5 py-4 shadow-xl backdrop-blur sm:-left-8"
              initial={{ opacity: 0, x: -24 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7, delay: 1.05 }}
              data-testid="hero-chip-pillars"
            >
              <p className="font-display text-2xl font-extrabold text-navy">
                10 Pillars
              </p>
              <p className="text-xs font-bold uppercase tracking-wider text-slate-500">
                One Window
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 24 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7, delay: 1.2 }}
              className="absolute -right-2 bottom-16 rounded-2xl bg-navy px-5 py-4 text-white shadow-xl sm:-right-6"
              data-testid="hero-chip-seamless"
            >
              <p className="font-display text-lg font-extrabold text-gold">
                Seamless
              </p>
              <p className="text-xs font-bold uppercase tracking-wider text-white/70">
                Single-Window Processing
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      <Marquee items={MARQUEE_ITEMS} />

      {/* INTRODUCTION */}
      <section className="border-b border-slate-200 py-16 sm:py-24" data-testid="home-intro-section">
        <div className="container-x">
          <Reveal>
            <p className="eyebrow">Comprehensive Consultancy</p>
          </Reveal>
          <div className="mt-3 grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-16">
            <Reveal className="lg:col-span-5">
              <h2 className="font-display text-3xl font-extrabold tracking-tight text-navy sm:text-4xl lg:text-5xl">
                One unified strategy for every milestone.
              </h2>
              <p className="mt-6 font-display text-lg font-bold text-navy/80">
                Building Wealth. <span className="text-amber-600">Scaling Brands.</span> Designing Spaces.
              </p>
            </Reveal>
            <Reveal delay={0.12} className="lg:col-span-7 lg:pt-1.5">
              <div className="space-y-5 text-base leading-relaxed text-slate-600 sm:text-lg">
                <p>
                  At Evolve Elite, we believe that scaling a modern business or achieving personal financial milestones
                  requires a unified strategy. As a comprehensive consultancy, we eliminate the friction of growth by
                  bringing financial advisory, digital IT services, and architectural design under one roof.
                </p>
                <p>
                  Whether you need to secure a multi-crore commercial loan, automate your lead generation via WhatsApp,
                  run high-converting Meta marketing campaigns, or execute a turnkey interior design project, our experts
                  deliver tailored solutions.
                </p>
                <p className="font-bold text-navy">
                  We do not just process applications; we engineer holistic ecosystems that empower you to build wealth,
                  scale your digital footprint, and design visionary spaces.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* SEVEN PILLARS */}
      <section
        className="py-16 sm:py-24 lg:py-32"
        data-testid="pillars-section"
      >
        <div className="container-x">
          <div className="grid grid-cols-1 gap-6 md:grid-cols-12 md:items-end md:gap-10">
            <Reveal className="md:col-span-7">
              <p className="eyebrow">What we do</p>
              <h2 className="mt-3 max-w-xl font-display text-3xl font-extrabold tracking-tight text-navy sm:text-4xl lg:text-5xl">
                Ten pillars. One accountable team.
              </h2>
            </Reveal>
            <Reveal delay={0.15} className="md:col-span-5 md:pb-2">
              <p className="max-w-md text-base leading-relaxed text-slate-600 sm:text-lg">
                Every pillar is a specialist desk — connected to the others, so
                your capital, brand and spaces always move together.
              </p>
            </Reveal>
          </div>

          <div
            className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4"
            data-testid="pillars-grid"
          >
            {PILLARS.map((p, i) => (
              <Reveal
                key={p.n}
                delay={0.06 * i}
                className={i === 0 || i === PILLARS.length - 1 ? "sm:col-span-2" : ""}
              >
                <Link
                  to={p.to}
                  className="card-lift group flex h-full flex-col justify-between rounded-3xl border border-slate-200 bg-white p-7 sm:p-8"
                  data-testid={`pillar-card-${p.n}`}
                >
                  <div className="flex items-start justify-between">
                    <p.icon className="h-8 w-8 text-gold-deep" />
                    <span className="font-mono text-xs font-semibold text-slate-400">
                      {p.n}
                    </span>
                  </div>
                  <div className="mt-12 flex flex-col">
                    <h3 className="font-display text-xl font-extrabold leading-snug tracking-tight text-navy sm:text-2xl">
                      {p.title}
                    </h3>
                    <p className="mt-2.5 text-sm leading-relaxed text-slate-600">
                      {p.desc}
                    </p>
                    <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-bold text-gold-deep">
                      Explore
                      <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* AHMEDABAD EXPERTISE */}
      <Deliverables />

      <section
        className="border-y border-slate-200 bg-[#F8FAFC] py-16 sm:py-24 lg:py-32"
        data-testid="ahmedabad-section"
      >
        <div className="container-x">
          <Reveal className="mx-auto max-w-3xl text-center">
            <p className="eyebrow flex items-center justify-center gap-3">
              <span className="inline-block h-px w-10 bg-amber-600/60" />
              Rooted in Ahmedabad
              <span className="inline-block h-px w-10 bg-amber-600/60" />
            </p>
            <h2 className="mt-3 font-display text-3xl font-extrabold tracking-tight text-navy sm:text-4xl lg:text-5xl">
              Local expertise. Seamless processing.
            </h2>
            <p className="mx-auto mt-5 max-w-2xl text-base leading-relaxed text-slate-600 sm:text-lg">
              We are an Ahmedabad consultancy through and through — fluent in
              the city's lenders, authorities and business networks, and
              obsessed with process. You brief one team; we carry the paperwork,
              the follow-ups and the deadlines.
            </p>
          </Reveal>
          <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-3">
            {[
              "Single-window processing across all ten services",
              "Deep local networks — lenders, insurers, authorities and vendors",
              "One accountable team, from sanction letters to site handover",
            ].map((point, i) => (
              <Reveal key={point} delay={0.1 + i * 0.1}>
                <div className="flex h-full items-start gap-3 rounded-2xl border border-slate-200 bg-white p-5">
                  <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-500" />
                  <p className="font-bold text-navy">{point}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="py-16 sm:py-24" data-testid="stats-band">
        <div className="container-x grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-16">
          <Reveal className="lg:col-span-4">
            <p className="eyebrow">Why Evolve Elite</p>
            <h2 className="mt-3 font-display text-3xl font-extrabold tracking-tight text-navy sm:text-4xl">
              Growth, measured from every angle.
            </h2>
            <p className="mt-5 max-w-sm text-base leading-relaxed text-slate-600">
              One consultancy, ten specialist pillars and a single accountable team — so your ambition never waits on a
              hand-off.
            </p>
          </Reveal>
          <div className="grid grid-cols-1 overflow-hidden rounded-3xl border border-slate-200 bg-white sm:grid-cols-2 lg:col-span-8">
            {STATS.map((s, i) => (
              <Reveal key={s.label} delay={i * 0.08} className="h-full">
                <div
                  className={`group relative flex h-full flex-col justify-between p-7 transition-colors duration-300 hover:bg-[#F8FAFC] sm:p-9 ${
                    i % 2 === 0 ? "sm:border-r border-slate-200" : ""
                  } ${i < 2 ? "border-b border-slate-200" : ""}`}
                  data-testid={`stat-${i + 1}`}
                >
                  <span className="absolute left-0 top-7 h-10 w-1 rounded-r-full bg-gold opacity-0 transition-opacity duration-300 group-hover:opacity-100 sm:top-9" />
                  <div className="flex items-baseline justify-between gap-4">
                    <p className="font-display text-5xl font-black tracking-tight text-navy sm:text-6xl">{s.value}</p>
                    <span className="font-mono text-xs font-semibold text-slate-400">0{i + 1}</span>
                  </div>
                  <div className="mt-6">
                    <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.22em] text-amber-600">{s.label}</p>
                    <p className="mt-2 text-sm leading-relaxed text-slate-600">{s.desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CALLBACK */}
      <section
        id="callback"
        className="grain relative overflow-hidden bg-navy py-16 sm:py-24"
        data-testid="callback-section"
      >
        <div
          className="pointer-events-none absolute -right-32 top-1/2 h-[420px] w-[420px] -translate-y-1/2 rounded-full bg-gold/20 blur-[110px]"
          aria-hidden="true"
        />
        <div className="container-x relative z-10 grid grid-cols-1 items-center gap-12 lg:grid-cols-2">
          <Reveal>
            <p className="eyebrow !text-gold">Start the conversation</p>
            <h2 className="mt-3 font-display text-3xl font-extrabold tracking-tight text-white sm:text-4xl lg:text-5xl">
              Talk to a specialist today.
            </h2>
            <p className="mt-5 max-w-md text-base leading-relaxed text-white/70 sm:text-lg">
              Leave your number and the right desk calls you back — usually the
              same working day. No queues, no call-centre runaround.
            </p>
            <div className="mt-8 space-y-3">
              {[
                "Direct line to the specialist desk, not a call centre",
                "Clear next steps and document list on the first call",
                "Your details stay with Evolve Elite — always",
              ].map((point) => (
                <p
                  key={point}
                  className="flex items-start gap-3 text-sm font-bold text-white/85"
                >
                  <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-gold" />
                  {point}
                </p>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <div
              className="rounded-3xl border border-white/10 bg-white p-6 shadow-2xl sm:p-8"
              data-testid="callback-form-card"
            >
              <LeadForm
                source="home-callback-section"
                fields={["name", "phone", "email", "service", "message"]}
                cta="Request a Callback"
                idPrefix="callback-form"
              />
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
